import * as React from 'react';
import { styled } from '@linaria/react';

import DottedControlBackground from '@/components/Controls/DottedControlBackground';
import { calculateSpringPosition } from './SpringPhysics.helpers';

const WIDTH = 300;
const HEIGHT = WIDTH;

interface Props {
  stiffness: number;
  damping: number;
  mass: number;
}

function SpringGraph({ stiffness, damping, mass }: Props) {
  const path = generatePath(stiffness, damping, mass);

  return (
    <Wrapper>
      <YLabel>
        <span>— Progress —</span>
      </YLabel>
      <XLabel>— Time —</XLabel>
      <Background numOfRows={10} numOfCols={10} />
      <Svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`}>
        <SpringPath d={path} />
      </Svg>
    </Wrapper>
  );
}

function generatePath(
  stiffness: number,
  damping: number,
  mass: number
): string {
  const duration = 2000;
  const numPoints = 200;
  const timeStep = duration / (numPoints - 1);

  const initialDisplacement = -HEIGHT / 2;

  // Generate points along the spring curve
  const points: Array<{ x: number; y: number }> = [];

  for (let i = 0; i < numPoints; i++) {
    const elapsed = i * timeStep;
    const springPosition = calculateSpringPosition(
      initialDisplacement,
      stiffness,
      damping,
      mass,
      elapsed
    );

    /*
      NOTE: For calculating the simulated points for LinearDemo, here’s how I did it:
      stopshipPoints.push(normalize(springPosition, -150, 0, 0, 100));
    */

    // Map time to x-coordinate (0 to WIDTH)
    const x = (i / (numPoints - 1)) * WIDTH;

    // Map spring position to y-coordinate (center of HEIGHT)
    // Invert y so that positive displacement goes up
    const y = HEIGHT / 2 - springPosition;

    points.push({ x, y });
  }

  // Convert points to SVG path
  if (points.length === 0) {
    return '';
  }

  const pathCommands = points.map((point, index) => {
    if (index === 0) {
      return `M ${point.x} ${point.y}`;
    } else {
      return `L ${point.x} ${point.y}`;
    }
  });

  return pathCommands.join(' ');
}

const Wrapper = styled.div`
  position: relative;
  width: ${WIDTH}px;
  height: ${HEIGHT}px;
  border-radius: 4px;
  outline: 1px solid var(--color-gray-300);
  outline-offset: -1px;
`;

const Label = styled.div`
  font-size: 0.75rem;
  font-weight: var(--font-weight-bold);
  color: var(--color-gray-700);
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const YLabel = styled(Label)`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;

  span {
    display: block;
    white-space: nowrap;
    transform: translateX(-1.5rem) rotate(-90deg);
    transform-origin: center;
  }
`;

const XLabel = styled(Label)`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 1rem;
  line-height: 1rem;
  display: flex;
  justify-content: center;
  transform: translateY(150%);
`;
const Background = styled(DottedControlBackground)`
  position: absolute;
  inset: 0;
`;

const Svg = styled.svg`
  position: absolute;
  inset: 8px;
  width: calc(100% - 16px);
  height: calc(100% - 16px);
  display: block;
  overflow: visible;
`;

const SpringPath = styled.path`
  fill: none;
  stroke: white;
  stroke-width: 5px;
  stroke-linecap: round;
  stroke-linejoin: round;
`;

export default React.memo(SpringGraph);
